import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import PageLayout from "../../components/PageLayout";
import { setLoading, showToast } from "../../store/slices/common";
import {
  clearFilmFeed,
  endActiveMatches,
  getFilmFeed,
  restartFilmFeed,
  searchFilmFeed,
  setFilmFeed,
} from "../../http/filmFeed";
import ConfirmDialog from "../CuratedMatches/ConfirmDialog";

const KIND_LABELS = {
  challenge: "Challenge",
  karaoke: "Song",
  dance: "Dance",
};

const targetKey = (item) => `${item.kind}:${item.id}`;

const formatWhen = (start, end) => {
  const fmt = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };
  const startLabel = fmt(start);
  const endLabel = fmt(end);
  if (startLabel && endLabel) return `${startLabel} – ${endLabel}`;
  return startLabel || endLabel || "—";
};

const formatCount = (value) => Number(value || 0).toLocaleString();

const FilmFeed = () => {
  const dispatch = useDispatch();
  const [filmFeed, setFilmFeedState] = useState(null);
  const [query, setQuery] = useState("");
  const [kind, setKind] = useState("all");
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [selected, setSelected] = useState([]);
  const [confirm, setConfirm] = useState(null);

  const selectedKeys = useMemo(
    () => new Set(selected.map(targetKey)),
    [selected]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      dispatch(setLoading(true));
    try {
      const res = await getFilmFeed();
      if (!cancelled) {
        setFilmFeedState(res?.data?.filmFeed || { enabled: false, targets: [] });
      }
    } catch {
      if (!cancelled) setFilmFeedState({ enabled: false, targets: [] });
    } finally {
        if (!cancelled) dispatch(setLoading(false));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  const runSearch = async (nextKind = kind) => {
    const q = query.trim();
    if (q.length < 2) {
      dispatch(showToast({ color: "error", msg: "Enter at least 2 characters." }));
      return;
    }
    dispatch(setLoading(true));
    try {
      const res = await searchFilmFeed({ q, kind: nextKind });
      const rows = res?.data?.results || [];
      setResults(rows);
      setSearched(true);
      setSelected((current) =>
        current.map((item) => rows.find((row) => targetKey(row) === targetKey(item)) || item)
      );
    } catch {
      // The request interceptor already shows the error.
    } finally {
      dispatch(setLoading(false));
    }
  };

  const toggle = (row) => {
    setSelected((current) => {
      const key = targetKey(row);
      if (current.some((item) => targetKey(item) === key)) {
        return current.filter((item) => targetKey(item) !== key);
      }
      if (!row.matchCount) return current;
      return [...current, row];
    });
  };

  const playableRows = results.filter((row) => row.matchCount > 0);
  const allPlayableSelected =
    playableRows.length > 0 && playableRows.every((row) => selectedKeys.has(targetKey(row)));

  const toggleAll = () => {
    setSelected((current) => {
      if (allPlayableSelected) {
        const visible = new Set(playableRows.map(targetKey));
        return current.filter((item) => !visible.has(targetKey(item)));
      }
      const next = [...current];
      const have = new Set(next.map(targetKey));
      playableRows.forEach((row) => {
        if (!have.has(targetKey(row))) next.push(row);
      });
      return next;
    });
  };

  const publish = async () => {
    const targets = selected
      .filter((item) => item.matchCount > 0)
      .map((item) => ({ kind: item.kind, id: item.id }));
    if (targets.length === 0) {
      dispatch(
        showToast({
          color: "error",
          msg: "Select a challenge or song that still has playable pagents.",
        })
      );
      return;
    }
    dispatch(setLoading(true));
    try {
      const res = await setFilmFeed(targets);
      const next = res?.data?.filmFeed;
      setFilmFeedState(next);
      const capped = next?.capped
        ? ` Showing ${formatCount(next.matchCount)} of ${formatCount(next.totalPlayable)}, biggest votes first.`
        : "";
      dispatch(
        showToast({
          color: "success",
          msg: `Dev VS feed is now those past pagents, with their vote counts.${capped}`,
        })
      );
    } catch {
      // The request interceptor already shows the error.
    } finally {
      dispatch(setLoading(false));
    }
  };

  const turnOff = async () => {
    dispatch(setLoading(true));
    try {
      const res = await clearFilmFeed();
      setFilmFeedState(res?.data?.filmFeed || { enabled: false, targets: [] });
      dispatch(showToast({ color: "success", msg: "The dev VS feed is back to normal." }));
    } catch {
      // The request interceptor already shows the error.
    } finally {
      dispatch(setLoading(false));
    }
  };

  const endActive = async () => {
    dispatch(setLoading(true));
    try {
      const res = await endActiveMatches();
      const ended = Number(res?.data?.ended) || 0;
      dispatch(
        showToast({
          color: "success",
          msg:
            ended === 1
              ? "Ended 1 active pagent."
              : `Ended ${formatCount(ended)} active pagents.`,
        })
      );
    } catch {
      // The request interceptor already shows the error.
    } finally {
      dispatch(setLoading(false));
    }
  };

  const restart = async () => {
    dispatch(setLoading(true));
    try {
      const res = await restartFilmFeed();
      setFilmFeedState(res?.data?.filmFeed);
      dispatch(
        showToast({
          color: "success",
          msg: "The next time the dev VS feed opens, it starts at the first pagent.",
        })
      );
    } catch {
      // The request interceptor already shows the error.
    } finally {
      dispatch(setLoading(false));
    }
  };

  const ask = (action) => {
    if (action === "publish") {
      const names = selected.map((item) => item.title).filter(Boolean);
      setConfirm({
        action,
        title: "Put these on the dev VS feed?",
        message: `The dev voting feed will show completed pagents from ${names.join(", ")}, with the vote counts they already have. The live feed comes back when you turn this off.`,
        confirmLabel: "Put on VS feed",
        confirmColor: "primary",
      });
      return;
    }
    if (action === "restart") {
      setConfirm({
        action,
        title: "Start the film from the first pagent?",
        message:
          "The next time someone opens the dev VS feed, playback starts again at the beginning of this queue.",
        confirmLabel: "Restart",
        confirmColor: "primary",
      });
      return;
    }
    if (action === "endActive") {
      setConfirm({
        action,
        title: "End every active pagent on dev?",
        message:
          "Every in-progress pagent is completed right now. The side ahead wins. A tie, including a pagent with no votes, is a draw. Vote totals stay as they are. This is dev only and does not run on production.",
        confirmLabel: "End all active pagents",
        confirmColor: "error",
      });
      return;
    }
    setConfirm({
      action: "clear",
      title: "Turn the film feed off?",
      message: "The dev VS feed goes back to the normal voting feed.",
      confirmLabel: "Turn off",
      confirmColor: "error",
    });
  };

  const onConfirm = async () => {
    const action = confirm?.action;
    setConfirm(null);
    if (action === "publish") await publish();
    if (action === "clear") await turnOff();
    if (action === "restart") await restart();
    if (action === "endActive") await endActive();
  };

  const active = filmFeed?.enabled;

  return (
    <PageLayout
      title="Film the VS Feed"
      subtitle="Search past challenges and songs, then put their completed pagents on the dev voting feed with the vote counts they finished with. Open the dev app to record them. Turning this off restores the normal feed. Vote totals are not changed."
    >
      {active ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            The dev VS feed is showing {formatCount(filmFeed.matchCount)} past pagents
            {filmFeed.capped
              ? ` (${formatCount(filmFeed.totalPlayable)} playable, biggest votes kept)`
              : ""}
            .
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, my: 1 }}>
            {(filmFeed.targets || []).map((item) => (
              <Chip
                key={targetKey(item)}
                size="small"
                label={`${item.title}${item.detail ? ` · ${item.detail}` : ""} · ${formatCount(item.matchCount)}`}
              />
            ))}
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button size="small" variant="outlined" onClick={() => ask("restart")}>
              Start from the first pagent
            </Button>
            <Button size="small" variant="contained" color="inherit" onClick={() => ask("clear")}>
              Turn off
            </Button>
          </Box>
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          Nothing is queued. The dev VS feed is the normal voting feed.
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          End active pagents
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Dev only. Completes every in-progress pagent immediately, instead of
          waiting for the timed end to close them one by one. The side ahead
          wins, and a tie is a draw.
        </Typography>
        <Button variant="outlined" color="error" onClick={() => ask("endActive")}>
          End all active pagents
        </Button>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            size="small"
            label="Search challenges and songs"
            placeholder="cloud"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") runSearch();
            }}
            sx={{ minWidth: 280, flex: 1 }}
          />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={kind}
            onChange={(_, value) => {
              if (!value) return;
              setKind(value);
              if (query.trim().length >= 2) runSearch(value);
            }}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="challenge">Challenges</ToggleButton>
            <ToggleButton value="karaoke">Songs</ToggleButton>
            <ToggleButton value="dance">Dances</ToggleButton>
          </ToggleButtonGroup>
          <Button variant="contained" onClick={() => runSearch()}>
            Search
          </Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          A search like cloud lists every matching challenge, including past makeup
          looks. Playable pagents are completed matches whose videos still play.
          Each pairing is included once, using the match with the most votes.
          Within a challenge or song, the biggest vote totals play first.
        </Typography>
      </Paper>

      {selected.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            Selected ({selected.length})
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.5 }}>
            {selected.map((item) => (
              <Chip
                key={targetKey(item)}
                label={`${item.title}${item.detail ? ` · ${item.detail}` : ""}`}
                onDelete={() => toggle(item)}
              />
            ))}
          </Box>
          <Button variant="contained" onClick={() => ask("publish")}>
            Put selected on the VS feed
          </Button>
        </Box>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allPlayableSelected}
                  disabled={playableRows.length === 0}
                  onChange={toggleAll}
                  inputProps={{ "aria-label": "Select all playable results" }}
                />
              </TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>When</TableCell>
              <TableCell align="right">Pagents</TableCell>
              <TableCell align="right">Votes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {results.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    {searched
                      ? "No challenges or songs matched that search."
                      : "Search for a challenge or song to see its past pagents."}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              results.map((row) => {
                const checked = selectedKeys.has(targetKey(row));
                return (
                  <TableRow
                    key={targetKey(row)}
                    hover
                    selected={checked}
                    onClick={() => toggle(row)}
                    sx={{ cursor: row.matchCount ? "pointer" : "default" }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={checked}
                        disabled={!row.matchCount}
                        inputProps={{ "aria-label": `Select ${row.title}` }}
                      />
                    </TableCell>
                    <TableCell>{KIND_LABELS[row.kind] || row.kind}</TableCell>
                    <TableCell>{row.title}</TableCell>
                    <TableCell>{row.detail || "—"}</TableCell>
                    <TableCell>{formatWhen(row.startDate, row.endDate)}</TableCell>
                    <TableCell align="right">{formatCount(row.matchCount)}</TableCell>
                    <TableCell align="right">{formatCount(row.votes)}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        confirmColor={confirm?.confirmColor}
        onCancel={() => setConfirm(null)}
        onConfirm={onConfirm}
      />
    </PageLayout>
  );
};

export default FilmFeed;
